// @ts-check
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { isPlatform } from '@ionic/react';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';

import { useClassroomContext } from 'Context/ClassroomContext';
import { useLearnCraftVideoPlayer } from 'Context/LearnCraftVideoPlayer';
import InformationButton from 'Components/VideoEndingOverlay/Button/InformationButton';
import { SvgIcon } from 'Components/Base';

import { useClearByOnDemand } from '../Contexts/ClearByOnDemandProvider/ClearByOnDemandProvider';
import ClearButtonView from '../Components/ClearButton';
import ClearToolTip from '../Components/ClearToolTip';
import useShowClearByOnDemandTooltip from '../Domain/useShowClearByOnDemandTooltip';
import { useByteArkSnapshot } from '../../../Domain/useByteArkSnapshot';
import { useMindstampSnapshot } from '../../../Domain/useMindstampSnapshot';
import useScreenCapture from '../../../Domain/useScreenCapture';
import useSubsectionContent from '../../../Domain/useSubsectionContent';

const ClearButtonVideoEndingOverlayView = ({
  onOpenClearModal,
  onClickVideoEndingOverlayContent,
}) => {
  const handleInformationContent = () => {
    onOpenClearModal();
    onClickVideoEndingOverlayContent();
  };

  return (
    <InformationButton
      title="สงสัยเนื้อหาไม่เข้าใจ"
      icon={SvgIcon.ICON_LIST.HELP}
      onClick={handleInformationContent}
      mb={3}
    />
  );
};

ClearButtonVideoEndingOverlayView.propTypes = {
  onOpenClearModal: PropTypes.func.isRequired,
  onClickVideoEndingOverlayContent: PropTypes.func,
};

const ClearButton = ({ renderButton, withTooltip = true }) => {
  const { classroomCourseId, currentSubsectionId, currentSubsectionReferId } =
    useClassroomContext();
  const [, , { goToFillingFormStep }, , { enableScreenshot }] = useClearByOnDemand();
  const [showTooltip, setShownTooltip] = useShowClearByOnDemandTooltip();
  const { capture } = useByteArkSnapshot();
  const { capture: mindstampSnapshot } = useMindstampSnapshot();
  const { capture: learnCraftCapture } = useLearnCraftVideoPlayer();
  const [, screenCapture] = useScreenCapture();

  const onCapture = useCallback(
    /**
     * @param {{screenshot?: any, videoTitle?: string, pageNo?: string, questionNo?: string}} formData
     */
    (formData) => {
      goToFillingFormStep(formData, {
        onGoToFillingFormStep: setShownTooltip,
      });
    },
    [goToFillingFormStep, setShownTooltip]
  );

  const { data: subsectionContentData } = useSubsectionContent({
    variables: {
      classroomCourseId,
      subSectionId: currentSubsectionId,
      subSectionReferId: currentSubsectionReferId,
    },
    fetchPolicy: 'cache-only',
  });

  const goToClearTicketForm = async () => {
    const contentProvider =
      subsectionContentData?.classroomSubSectionContent?.assets?.contentAssets?.[0]?.content
        ?.provider;

    if (contentProvider === 'BYTEARK' || (contentProvider === 'BYTEARK' && isPlatform('ios'))) {
      const screenshot = await capture();
      onCapture({ screenshot });
    } else if (contentProvider === 'MINDSTAMP') {
      const screenshot = await mindstampSnapshot();
      onCapture({ screenshot });
    } else if (contentProvider === 'LEARNCRAFT') {
      const result = await learnCraftCapture();
      if (!result) {
        onCapture({});
      } else {
        const formData = {
          screenshot: result.image,
          videoTitle: result.chapterName,
          pageNo: result.pageNo,
          questionNo: result.problemNo,
        };
        onCapture(pickBy(formData, identity));
      }
    } else {
      const app = document.getElementById('app');
      const video = document.querySelector('video');
      const element = video ?? app;
      return await screenCapture(element, { onCapture });
    }
  };

  const goToClearTicketFormWithoutScreenCapture = useCallback(
    () => goToFillingFormStep(null, { onGoToFillingFormStep: setShownTooltip }),
    [goToFillingFormStep, setShownTooltip]
  );

  const handleOpenClearModal = () => {
    if (enableScreenshot) {
      goToClearTicketForm();
    } else {
      goToClearTicketFormWithoutScreenCapture();
    }
  };

  if (!withTooltip) {
    return (
      <>
        {typeof renderButton === 'function' ? (
          renderButton(handleOpenClearModal)
        ) : (
          <ClearButtonView onClick={handleOpenClearModal} />
        )}
      </>
    );
  }

  return (
    <ClearToolTip isShow={!!showTooltip} onClick={setShownTooltip}>
      {typeof renderButton === 'function' ? (
        renderButton(handleOpenClearModal)
      ) : (
        <ClearButtonView onClick={handleOpenClearModal} />
      )}
    </ClearToolTip>
  );
};

ClearButton.propTypes = {
  renderButton: PropTypes.func,
  withTooltip: PropTypes.bool,
};

export default ClearButton;
